import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MENU_RENTAL_PORTAL_ITEM } from '../../../constants/menu'
import { ROUTE_PATH_LIST_RENTAL_PORTAL } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { usePageBrowserTabTitle } from '../../../hooks'
import Button from '../../Button'
import { MultipleViewTypeListLayout } from '../../MultipleViewTypeList'
import { Page } from '../../Page'
import { PageEmptyState } from '../../PageEmptyState'
import { PageHeader } from '../../PageHeader'
import { Table } from '../../Table'
import Text from '../../Text'

import { IntegrationsPreviewEmpty } from './IntegrationsPreviewEmpty'
import { IntegrationsPreviewModal } from './IntegrationsPreviewModal'
import {
  INTEGRATIONS_COLUMNS_CONFIG,
  INTEGRATIONS_SKELETON_ROWS,
  QA_NAME_INTEGRATIONS_PANEL,
  QA_NAME_INTEGRATIONS_RETRY,
  QA_NAME_RENTAL_PORTAL_INTEGRATIONS,
} from './RentalPortalIntegrations.constants'
import {
  useRentalPortalIntegrationsRead,
  useRentalPortalIntegrationsViewRegime,
} from './RentalPortalIntegrations.hooks'
import {
  getIntegrationsCanConfigure,
  getIntegrationsPortalById,
  getIntegrationsPortals,
  getIsIntegrationsEmpty,
} from './RentalPortalIntegrations.utils'
import { RentalPortalIntegrationsPanel } from './RentalPortalIntegrationsPanel'
import { RentalPortalIntegrationsRow } from './RentalPortalIntegrationsRow'

import RefreshIcon from '../../../icons/refresh.svg'
import SettingsIcon from '../../../icons/settings.svg'

function RentalPortalIntegrations() {
  const { t } = useTranslation()

  const [portalColumns, setPortalColumns] = useState(
    INTEGRATIONS_COLUMNS_CONFIG
  )

  const pageScrollWrapperRef = useRef(null)

  const { handleReload, handleRetry, hasError, integrations, isLoading } =
    useRentalPortalIntegrationsRead()

  const portals = getIntegrationsPortals(integrations)

  const canConfigure = getIntegrationsCanConfigure(integrations)

  const {
    activeItem,
    handleCloseModal,
    handlePreviewResizeEnd,
    handleResetViewTypeToPlainBody,
    handleSelectPortal,
    listViewType,
    modalPortal,
    preview,
  } = useRentalPortalIntegrationsViewRegime(portals)

  const title = t('RentalPortal.IntegrationsTitleKey')

  usePageBrowserTabTitle({ isLoading, pageTitle: title })

  const breadcrumbs = [
    {
      id: MENU_RENTAL_PORTAL_ITEM.title,
      title: t(MENU_RENTAL_PORTAL_ITEM.title),
      to: `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}`,
    },
    { id: title, title },
  ]

  return (
    <MultipleViewTypeListLayout
      listViewType={listViewType}
      pageScrollWrapperRef={pageScrollWrapperRef}
      previewProps={{
        Component: RentalPortalIntegrationsPanel,
        Empty: IntegrationsPreviewEmpty,
        activeItem,
        canResize: true,
        componentProps: {
          canConfigure,
          connection: getIntegrationsPortalById({ activeItem, portals }),
          onChanged: handleReload,
          onClose: handleResetViewTypeToPlainBody,
        },
        emptyProps: {
          EntityIcon: SettingsIcon,
          text: t('RentalPortal.IntegrationsPreviewEmptyKey'),
          onClosePreview: handleResetViewTypeToPlainBody,
        },
        qaName: QA_NAME_INTEGRATIONS_PANEL,
        resizeConfig: {
          initialWidth: preview?.size,
          onResizeEnd: handlePreviewResizeEnd,
        },
      }}
    >
      <ScreenContainerRef value={pageScrollWrapperRef}>
        <Page
          qaName={QA_NAME_RENTAL_PORTAL_INTEGRATIONS}
          scrollWrapperRef={pageScrollWrapperRef}
        >
          <PageHeader
            breadcrumbs={breadcrumbs}
            isLoading={isLoading}
            title={title}
          />
          {hasError ? (
            <PageEmptyState EntityIcon={SettingsIcon}>
              <Text isSecondary>
                {t('RentalPortal.IntegrationsLoadErrorKey')}
              </Text>
              <Button.Flat
                beforeIconProps={{ Icon: RefreshIcon }}
                isLoading={isLoading}
                qaName={QA_NAME_INTEGRATIONS_RETRY}
                text={t('RentalPortal.ListingRetryBtnKey')}
                onClick={handleRetry}
              />
            </PageEmptyState>
          ) : getIsIntegrationsEmpty({ isLoading, portals }) ? (
            <PageEmptyState
              EntityIcon={SettingsIcon}
              text={t('RentalPortal.IntegrationsEmptyKey')}
            />
          ) : (
            <Table
              TableRow={RentalPortalIntegrationsRow}
              actionsColumnWidth={0}
              columns={portalColumns}
              context={QA_NAME_RENTAL_PORTAL_INTEGRATIONS}
              headerRowProps={{ onSetColumns: setPortalColumns }}
              isLoading={isLoading}
              items={portals}
              rowProps={{ activeItem, onClick: handleSelectPortal }}
              skeletonProps={{ withCheckbox: false }}
              skeletonRows={INTEGRATIONS_SKELETON_ROWS}
            />
          )}
        </Page>
      </ScreenContainerRef>
      {modalPortal ? (
        <IntegrationsPreviewModal
          canConfigure={canConfigure}
          connection={getIntegrationsPortalById({
            activeItem: modalPortal,
            portals,
          })}
          onChanged={handleReload}
          onClose={handleCloseModal}
        />
      ) : null}
    </MultipleViewTypeListLayout>
  )
}

export default RentalPortalIntegrations
